import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import AppLayout from '../components/AppLayout';
import './Transfers.css';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

export default function Transfers() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [selectedFromIban, setSelectedFromIban] = useState('');
  const [form, setForm] = useState({ toIban: '', amount: '', label: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [destInfo, setDestInfo] = useState(null);
  const [destLoading, setDestLoading] = useState(false);

  const loadAccounts = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    const list = [];
    try {
      const res = await axios.get('/api/bank/account', { headers });
      list.push({ type: 'personal', label: 'Mon compte personnel', iban: res.data.iban, balance: res.data.balance, icon: 'fa-user' });
    } catch (_) {}
    try {
      const res = await axios.get('/api/company/accounts', { headers });
      for (const c of res.data || []) {
        const name = c.society_name || c.account_name || c.iban;
        list.push({ type: 'company', label: `Caisse — ${name}`, iban: c.iban, balance: c.balance, icon: 'fa-briefcase' });
      }
    } catch (_) {}
    setAccounts(list);
    if (list.length > 0) setSelectedFromIban(l => l || list[0].iban);
  }, [token]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const selectedAccount = accounts.find(a => a.iban === selectedFromIban);

  // Lookup IBAN destinataire avec debounce
  useEffect(() => {
    const iban = form.toIban.trim().toUpperCase();
    if (iban.length < 4) { setDestInfo(null); return; }
    const timer = setTimeout(async () => {
      setDestLoading(true);
      try {
        const res = await axios.get(`/api/bank/lookup-iban?iban=${encodeURIComponent(iban)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDestInfo(res.data);
      } catch (_) { setDestInfo(null); }
      finally { setDestLoading(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.toIban, token]);

  const handleChange = (e) => { setForm(f => ({ ...f, [e.target.name]: e.target.value })); setError(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.toIban.trim()) { setError('Veuillez saisir un IBAN destinataire'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Montant invalide'); return; }
    if (!selectedFromIban) { setError('Sélectionnez un compte source'); return; }
    setLoading(true); setError(null);
    try {
      const res = await axios.post('/api/bank/transfer', {
        toIban: form.toIban.trim().toUpperCase(),
        fromIban: selectedFromIban,
        amount: parseFloat(form.amount),
        label: form.label.trim() || 'Virement',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess({ ...res.data, amount: parseFloat(form.amount), toIban: form.toIban.trim().toUpperCase(), destName: res.data.destName || destInfo?.name, fromAccount: selectedAccount });
      setForm({ toIban: '', amount: '', label: '' });
      setDestInfo(null);
      loadAccounts();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors du virement');
    } finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><i className="fa-solid fa-arrow-left" /></button>
        <div>
          <h1 className="page-title">Nouveau virement</h1>
          <p className="page-subtitle">Transférez des fonds entre comptes personnels ou sociétés</p>
        </div>
      </div>

      <div className="transfer-layout">
        <div className="card transfer-form-card">
          <h2 className="transfer-section-title"><i className="fa-solid fa-paper-plane" /> Détails du virement</h2>

          {success ? (
            <div className="transfer-success">
              <div className="success-icon"><i className="fa-solid fa-circle-check" /></div>
              <h3>Virement {success.pending ? 'enregistré' : 'effectué'}</h3>
              <p>
                <strong>{formatAmount(success.amount)}</strong> depuis <em>{success.fromAccount?.label}</em> vers <code>{success.toIban}</code>
                {success.destName && <> ({success.destName})</>}
              </p>
              {success.pending ? (
                <p className="success-pending"><i className="fa-solid fa-clock" /> Sera appliqué en jeu dans quelques instants (ou à votre prochaine connexion).</p>
              ) : (
                success.newBalance != null && <p className="success-balance">Nouveau solde : <strong>{formatAmount(success.newBalance)}</strong></p>
              )}
              <div className="success-actions">
                <button className="btn-primary" onClick={() => setSuccess(null)}><i className="fa-solid fa-plus" /> Nouveau virement</button>
                <button className="btn-secondary" onClick={() => navigate('/history')}><i className="fa-solid fa-clock-rotate-left" /> Historique</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="transfer-form">

              {/* Compte source */}
              <div className="form-group">
                <label className="form-label"><i className="fa-solid fa-wallet" /> Compte émetteur</label>
                <div className="account-selector">
                  {accounts.map(acc => (
                    <button key={acc.iban} type="button"
                      className={`account-option ${selectedFromIban === acc.iban ? 'active' : ''}`}
                      onClick={() => { setSelectedFromIban(acc.iban); setError(null); }}
                    >
                      <div className={`account-option-icon ${acc.type === 'company' ? 'icon-company' : 'icon-personal'}`}>
                        <i className={`fa-solid ${acc.icon}`} />
                      </div>
                      <div className="account-option-info">
                        <span className="account-option-label">{acc.label}</span>
                        <span className="account-option-balance">{formatAmount(acc.balance)}</span>
                      </div>
                      {selectedFromIban === acc.iban && <i className="fa-solid fa-circle-check account-option-check" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* IBAN destinataire */}
              <div className="form-group">
                <label className="form-label"><i className="fa-solid fa-credit-card" /> IBAN destinataire</label>
                <input type="text" name="toIban" value={form.toIban} onChange={handleChange}
                  placeholder="FLXXXXXX" className={`input-field ${destInfo ? 'input-valid' : ''}`} required autoComplete="off" />
                {destLoading && <span className="dest-lookup"><i className="fa-solid fa-spinner spinner" /> Recherche...</span>}
                {destInfo && !destLoading && (
                  <span className="dest-found">
                    <i className={`fa-solid ${destInfo.type === 'company' ? 'fa-briefcase' : 'fa-user'}`} />
                    {destInfo.name}
                    {destInfo.type === 'company' && <span className="dest-badge-company">Entreprise</span>}
                  </span>
                )}
                {!destInfo && !destLoading && form.toIban.trim().length >= 4 && (
                  <span className="dest-not-found"><i className="fa-solid fa-circle-xmark" /> IBAN introuvable</span>
                )}
              </div>

              {/* Montant */}
              <div className="form-group">
                <label className="form-label"><i className="fa-solid fa-dollar-sign" /> Montant ($)</label>
                <div className="amount-input-wrap">
                  <input type="number" name="amount" value={form.amount} onChange={handleChange}
                    placeholder="0" min="1" step="1" className="input-field amount-input" required />
                  <span className="amount-currency">$</span>
                </div>
                {selectedAccount && form.amount && parseFloat(form.amount) > selectedAccount.balance && (
                  <span className="amount-warning"><i className="fa-solid fa-triangle-exclamation" /> Solde insuffisant ({formatAmount(selectedAccount.balance)} disponible)</span>
                )}
              </div>

              {/* Libellé */}
              <div className="form-group">
                <label className="form-label"><i className="fa-solid fa-tag" /> Libellé <span className="label-optional">(optionnel)</span></label>
                <input type="text" name="label" value={form.label} onChange={handleChange}
                  placeholder="Ex: Remboursement, salaire..." className="input-field" maxLength={100} />
              </div>

              {error && <div className="form-error"><i className="fa-solid fa-circle-exclamation" /> {error}</div>}

              <button type="submit" className="btn-primary transfer-submit-btn" disabled={loading}>
                {loading ? <><i className="fa-solid fa-spinner spinner" /> Envoi en cours...</> : <><i className="fa-solid fa-paper-plane" /> Envoyer le virement</>}
              </button>
            </form>
          )}
        </div>

        {/* Info panel */}
        <div className="transfer-info-panel">
          <div className="card transfer-warning">
            <i className="fa-solid fa-triangle-exclamation" />
            <div><strong>Attention</strong><p>Vérifiez l'IBAN du destinataire avant d'envoyer. Les virements ne peuvent pas être annulés.</p></div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

